import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../services/api';

const PERM_FIELDS = [
  { k: 'can_create',     l: 'Rezepte erstellen' },
  { k: 'can_edit_own',   l: 'Eigene bearbeiten' },
  { k: 'can_edit_all',   l: 'Alle bearbeiten' },
  { k: 'can_delete_own', l: 'Eigene löschen' },
  { k: 'can_delete_all', l: 'Alle löschen' },
  { k: 'can_view_all',   l: 'Alle sehen' },
];

export default function UserEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error, setError]       = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [recipeCount, setRecipeCount] = useState(null);
  const [quota, setQuota]     = useState(null);   // { scan_limit_override, effective_limit, used_this_month, unlimited }
  const [quotaInput, setQuotaInput] = useState('');
  const [quotaSaving, setQuotaSaving] = useState(false);
  const [fridgeQuota, setFridgeQuota] = useState(null);
  const [fridgeResetting, setFridgeResetting] = useState(false);
  const [form, setForm] = useState({
    username: '', password: '', email: '', is_admin: false,
    can_create: true, can_edit_own: true, can_edit_all: false,
    can_delete_own: true, can_delete_all: false, can_view_all: true,
  });

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    async function loadUser() {
      try {
        const [ud, rd, qd, fqd] = await Promise.all([
          api.getUser(id),
          api.getRecipes({ user_id: id, limit: 1 }),
          api.getUserQuota(id),
          api.getAdminFridgeQuota(id),
        ]);
        const u = ud.user;
        const perms = u.permissions || {};
        setForm({
          username:       u.username,
          password:       '',
          email:          u.email || '',
          is_admin:       !!u.is_admin,
          can_create:     perms.can_create     ?? true,
          can_edit_own:   perms.can_edit_own   ?? true,
          can_edit_all:   perms.can_edit_all   ?? false,
          can_delete_own: perms.can_delete_own ?? true,
          can_delete_all: perms.can_delete_all ?? false,
          can_view_all:   perms.can_view_all   ?? true,
        });
        setRecipeCount(rd.total ?? null);
        setQuota(qd);
        setQuotaInput(qd.scan_limit_override != null ? String(qd.scan_limit_override) : '');
        setFridgeQuota(fqd);
      } catch {
        setError('Benutzer nicht gefunden');
      } finally {
        setLoading(false);
      }
    }
    loadUser();
  }, [id]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  async function handleSave(e) {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      const perms = {};
      PERM_FIELDS.forEach(p => perms[p.k] = form[p.k]);
      if (isNew) {
        if (!form.username.trim()) throw new Error('Benutzername erforderlich');
        if (!form.password)        throw new Error('Passwort erforderlich');
        await api.createUser({ username: form.username, password: form.password, email: form.email, is_admin: form.is_admin, permissions: perms });
      } else {
        const upd = { email: form.email, is_admin: form.is_admin };
        if (form.password) upd.password = form.password;
        await api.updateUser(id, upd);
        await api.updatePermissions(id, perms);
      }
      navigate('/admin/users');
    } catch(e) { setError(e.message || 'Fehler beim Speichern'); }
    finally    { setSaving(false); }
  }

  async function handleSaveQuota(resetToDefault) {
    setQuotaSaving(true);
    try {
      const override = resetToDefault ? null : (quotaInput === '' ? null : parseInt(quotaInput));
      await api.setUserQuota(id, override);
      const qd = await api.getUserQuota(id);
      setQuota(qd);
      setQuotaInput(qd.scan_limit_override != null ? String(qd.scan_limit_override) : '');
    } catch(e) { setError(e.message); }
    finally    { setQuotaSaving(false); }
  }

  async function handleResetFridgeQuota() {
    setFridgeResetting(true);
    try {
      await api.resetAdminFridgeQuota(id);
      const fqd = await api.getAdminFridgeQuota(id);
      setFridgeQuota(fqd);
    } catch(e) { setError(e.message || 'Fehler beim Zurücksetzen'); }
    finally    { setFridgeResetting(false); }
  }

  async function handleDelete() {
    setShowDeleteConfirm(false);
    try { await api.deleteUser(id); navigate('/admin/users'); }
    catch(e) { setError(e.message); }
  }

  const c = {
    wrap:    { padding: '24px 32px', maxWidth: 1000 },
    bc:      { fontSize: '.7rem', color: 'var(--admin-text-dim)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 },
    bcLink:  { color: 'var(--admin-text-muted)', textDecoration: 'none' },
    bcSep:   { color: 'var(--admin-text-dim)' },
    h1:      { fontSize: '1.4rem', color: 'var(--admin-text)', fontFamily: 'Inter,sans-serif', fontWeight: 600, marginBottom: 24 },
    cols:    { display: 'flex', gap: 20, alignItems: 'flex-start' },
    main:    { flex: '1 1 0%' },
    side:    { width: 260, flexShrink: 0 },
    card:    { background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', borderRadius: 8, marginBottom: 16, overflow: 'hidden' },
    cardHd:  { padding: '10px 16px', background: 'var(--admin-bg-dark)', borderBottom: '1px solid var(--admin-border)', fontSize: '.72rem', fontWeight: 600, color: 'var(--admin-text-muted)', textTransform: 'uppercase', letterSpacing: '.07em' },
    cardBd:  { padding: 16 },
    lbl:     { display: 'block', fontSize: '.68rem', color: 'var(--admin-text-muted)', marginBottom: 4, marginTop: 12 },
    inp:     { width: '100%', background: 'var(--admin-bg-input)', border: '1px solid var(--admin-border)', borderRadius: 5, color: 'var(--admin-text)', padding: '8px 10px', fontSize: '.8rem', boxSizing: 'border-box' },
    sel:     { width: '100%', background: 'var(--admin-bg-input)', border: '1px solid var(--admin-border)', borderRadius: 5, color: 'var(--admin-text)', padding: '8px 10px', fontSize: '.8rem', boxSizing: 'border-box' },
    togRow:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--admin-tag-bg)' },
    togLbl:  { fontSize: '.78rem', color: 'var(--admin-text-muted)' },
    togDesc: { fontSize: '.65rem', color: 'var(--admin-text-dim)', marginTop: 2 },
    saveBtn: { width: '100%', background: 'var(--admin-btn-primary)', border: 'none', color: '#fff', borderRadius: 6, padding: '10px', fontSize: '.82rem', fontWeight: 600, cursor: 'pointer' },
    cancelBtn: { width: '100%', background: 'none', border: '1px solid var(--admin-border)', color: 'var(--admin-text-muted)', borderRadius: 6, padding: '8px', fontSize: '.78rem', cursor: 'pointer', marginTop: 6 },
    delBtn:  { width: '100%', background: 'none', border: '1px solid var(--admin-danger)', color: 'var(--admin-danger)', borderRadius: 6, padding: '8px', fontSize: '.75rem', cursor: 'pointer', marginTop: 6 },
    errBox:  { background: 'color-mix(in srgb, var(--admin-danger) 10%, transparent)', border: '1px solid var(--admin-danger)', borderRadius: 5, padding: '8px 12px', fontSize: '.75rem', color: 'var(--admin-danger)', marginBottom: 16 },
    statBox: { display: 'flex', gap: 10, marginBottom: 4 },
    stat:    { flex: 1, background: 'var(--admin-bg-input)', border: '1px solid var(--admin-border)', borderRadius: 6, padding: '10px', textAlign: 'center' },
    statV:   { fontSize: '1.4rem', fontWeight: 700, color: 'var(--admin-accent)' },
    statL:   { fontSize: '.62rem', color: 'var(--admin-text-dim)', textTransform: 'uppercase', letterSpacing: '.05em' },
  };

  if (loading) return <div style={{...c.wrap, color:'var(--admin-text-dim)'}}>Lade Benutzer...</div>;

  return (
    <div style={c.wrap}>
      <div style={c.bc}>
        <Link to="/admin" style={c.bcLink}>Admin</Link>
        <span style={c.bcSep}>›</span>
        <Link to="/admin/users" style={c.bcLink}>Benutzer</Link>
        <span style={c.bcSep}>›</span>
        <span style={{color:'var(--admin-text)'}}>{isNew ? 'Neuer Benutzer' : form.username}</span>
      </div>

      <h1 style={c.h1}>{isNew ? '+ Neuer Benutzer' : 'Benutzer bearbeiten'}</h1>

      {error && <div style={c.errBox}>{error}</div>}

      <form onSubmit={handleSave}>
        <div style={c.cols}>

          {/* ── Hauptspalte ── */}
          <div style={c.main}>
            <div style={c.card}>
              <div style={c.cardHd}>Zugangsdaten</div>
              <div style={c.cardBd}>
                {isNew && (
                  <>
                    <label style={{...c.lbl, marginTop:0}}>Benutzername *</label>
                    <input style={c.inp} value={form.username} onChange={e => set('username', e.target.value)} placeholder="min. 3 Zeichen" required />
                  </>
                )}
                <label style={isNew ? c.lbl : {...c.lbl, marginTop:0}}>
                  {isNew ? 'Passwort *' : 'Neues Passwort (leer = unverändert)'}
                </label>
                <input style={c.inp} type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder="min. 6 Zeichen" />
                <label style={c.lbl}>E-Mail</label>
                <input style={c.inp} type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="optional" />
              </div>
            </div>

            <div style={c.card}>
              <div style={c.cardHd}>Berechtigungen</div>
              <div style={c.cardBd}>
                {PERM_FIELDS.map(p => (
                  <div key={p.k} style={c.togRow}>
                    <span style={c.togLbl}>{p.l}</span>
                    <input type="checkbox" checked={!!form[p.k]} onChange={e => set(p.k, e.target.checked)} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Seitenleiste ── */}
          <div style={c.side}>

            <div style={c.card}>
              <div style={c.cardHd}>Speichern</div>
              <div style={c.cardBd}>
                <label style={{...c.lbl, marginTop:0}}>Rolle</label>
                <select style={c.sel} value={form.is_admin ? 'admin' : 'user'} onChange={e => set('is_admin', e.target.value === 'admin')}>
                  <option value="user">👤 Benutzer</option>
                  <option value="admin">⚙ Admin</option>
                </select>
                <button type="submit" style={{...c.saveBtn, marginTop: 12}} disabled={saving}>
                  {saving ? 'Speichert…' : (isNew ? '✓ Erstellen' : '✓ Speichern')}
                </button>
                <button type="button" style={c.cancelBtn} onClick={() => navigate('/admin/users')}>Abbrechen</button>
                {!isNew && (
                  showDeleteConfirm ? (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: '.73rem', color: 'var(--admin-danger)', marginBottom: 6 }}>
                        Benutzer <strong>„{form.username}"</strong> wirklich löschen?
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" style={{ ...c.delBtn, background: 'var(--admin-danger)', color: '#fff' }} onClick={handleDelete}>Ja, löschen</button>
                        <button type="button" style={c.cancelBtn} onClick={() => setShowDeleteConfirm(false)}>Abbrechen</button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" style={c.delBtn} onClick={() => { setError(''); setShowDeleteConfirm(true); }}>✕ Benutzer löschen</button>
                  )
                )}
              </div>
            </div>

            {!isNew && recipeCount !== null && (
              <div style={c.card}>
                <div style={c.cardHd}>Statistik</div>
                <div style={c.cardBd}>
                  <div style={c.statBox}>
                    <div style={c.stat}>
                      <div style={c.statV}>{recipeCount}</div>
                      <div style={c.statL}>Rezepte</div>
                    </div>
                  </div>
                  <Link to={`/admin/recipes?user_id=${id}`} style={{fontSize:'.7rem', color:'var(--admin-link)'}}>
                    Alle Rezepte dieses Nutzers →
                  </Link>
                </div>
              </div>
            )}

            {!isNew && fridgeQuota && (
              <div style={c.card}>
                <div style={c.cardHd}>Kühlschrank-Quota</div>
                <div style={c.cardBd}>
                  <div style={{fontSize:'.7rem', color:'var(--admin-text-muted)', marginBottom:8}}>
                    Paket: <strong>{fridgeQuota.pkg_label}</strong>
                  </div>
                  <div style={c.statBox}>
                    <div style={c.stat}>
                      <div style={{...c.statV, color: fridgeQuota.trial_used ? 'var(--admin-danger)' : 'var(--admin-accent)'}}>
                        {fridgeQuota.pkg === 'free'
                          ? (fridgeQuota.trial_used ? '✗' : '✓')
                          : fridgeQuota.scans_this_week}
                      </div>
                      <div style={c.statL}>{fridgeQuota.pkg === 'free' ? 'Trial' : 'Diese Woche'}</div>
                    </div>
                    <div style={c.stat}>
                      <div style={c.statV}>
                        {fridgeQuota.weekly_limit === 0 ? '∞' : fridgeQuota.weekly_limit}
                      </div>
                      <div style={c.statL}>Limit/Woche</div>
                    </div>
                  </div>
                  {fridgeQuota.week_start && (
                    <div style={{fontSize:'.65rem', color:'var(--admin-text-dim)', marginBottom:8}}>
                      Woche seit: {fridgeQuota.week_start}
                    </div>
                  )}
                  <button
                    type="button"
                    style={{...c.saveBtn, marginTop:4}}
                    onClick={handleResetFridgeQuota}
                    disabled={fridgeResetting}
                  >
                    {fridgeResetting ? '…' : '↺ Quota zurücksetzen'}
                  </button>
                </div>
              </div>
            )}

            {!isNew && quota && (
              <div style={c.card}>
                <div style={c.cardHd}>Scan-Quota</div>
                <div style={c.cardBd}>
                  <div style={c.statBox}>
                    <div style={c.stat}>
                      <div style={c.statV}>{quota.used_this_month}</div>
                      <div style={c.statL}>Genutzt</div>
                    </div>
                    <div style={c.stat}>
                      <div style={c.statV}>{quota.unlimited ? '∞' : quota.effective_limit}</div>
                      <div style={c.statL}>Limit</div>
                    </div>
                  </div>
                  {quota.scan_limit_override != null && (
                    <div style={{fontSize:'.65rem', color:'var(--admin-accent)', marginBottom:8}}>
                      ⚡ Individueller Override: {quota.scan_limit_override} Scans/Monat
                    </div>
                  )}
                  <label style={{...c.lbl, marginTop: 8}}>Override setzen (Scans/Monat)</label>
                  <input
                    style={c.inp}
                    type="number"
                    min="0"
                    value={quotaInput}
                    onChange={e => setQuotaInput(e.target.value)}
                    placeholder="leer = Paket-Standard"
                  />
                  <div style={{display:'flex', gap:6, marginTop:8}}>
                    <button
                      type="button"
                      style={{...c.saveBtn, flex:1}}
                      onClick={() => handleSaveQuota(false)}
                      disabled={quotaSaving}
                    >
                      {quotaSaving ? '…' : '✓ Setzen'}
                    </button>
                    <button
                      type="button"
                      style={{...c.cancelBtn, flex:1, margin:0}}
                      onClick={() => { setQuotaInput(''); handleSaveQuota(true); }}
                      disabled={quotaSaving}
                    >
                      Zurücksetzen
                    </button>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </form>
    </div>
  );
}
